import React, { useEffect, useState } from "react";
import axiosInstance from "../api/axiosConfig";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { Bar } from "react-chartjs-2";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ChartDataLabels
);

// Orden por defecto para la comparación RFM
const RFM_ORDER = [
  "AAA",
  "AAB",
  "AAC",
  "AAD",
  "ABA",
  "ABB",
  "ABC",
  "ABD",
  "BAA",
  "BAB",
  "BAC",
  "BAD",
];

const RFMComparisonDashboard = () => {
  const [reports, setReports] = useState([]);
  const [reportId1, setReportId1] = useState("");
  const [reportId2, setReportId2] = useState("");

  // Gráfica de comparación
  const [chartData, setChartData] = useState(null);
  // Gráfica de TOP 10
  const [top10ChartData, setTop10ChartData] = useState(null);

  // Opciones "dark" para la(s) gráfica(s)
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    color: "#ccc", // color general del texto
    plugins: {
      title: {
        display: true,
        text: "",
        font: { size: 16 },
        color: "#ccc",
      },
      legend: {
        position: "bottom",
        labels: {
          color: "#ccc", // color de la leyenda
        },
      },
      tooltip: {
        titleColor: "#ccc",
        bodyColor: "#ccc",
        backgroundColor: "#333",
        borderColor: "#666",
        borderWidth: 1,
      },
      datalabels: {
        display: true,
        anchor: "end",
        align: "end",
        color: "#ccc",
        font: {
          weight: "bold",
          size: 12,
        },
        formatter: (value) => value,
      },
    },
    scales: {
      x: {
        ticks: {
          color: "#ccc", // color de labels eje X
        },
        title: {
          display: true,
          text: "Códigos RFM",
          color: "#ccc",
        },
      },
      y: {
        beginAtZero: true,
        ticks: {
          color: "#ccc", // color de labels eje Y
        },
        title: {
          display: true,
          text: "Cantidad",
          color: "#ccc",
        },
      },
    },
  };

  // 1) Obtener lista de reportes
  useEffect(() => {
    axiosInstance
      .get("/reports")
      .then((res) => {
        setReports(res.data);
      })
      .catch((err) => {
        console.error("Error al obtener la lista de reportes:", err);
      });
  }, []);

  // 2) Comparar 2 reportes
  const handleCompare = () => {
    if (!reportId1 || !reportId2) return;

    axiosInstance
      .get(`/reports/rfm-compare?reportId1=${reportId1}&reportId2=${reportId2}`)
      .then((res) => {
        const { period1, period2, data } = res.data;

        // Asegurar que aparezcan todos los códigos RFM (según RFM_ORDER)
        const merged = RFM_ORDER.map((code) => {
          const found = data.find((item) => item.rfmCode === code);
          return {
            rfmCode: code,
            total1: found ? found.total1 : 0,
            total2: found ? found.total2 : 0,
          };
        });

        const labels = merged.map((item) => item.rfmCode);
        const dataset1 = merged.map((item) => item.total1);
        const dataset2 = merged.map((item) => item.total2);

        const newChartData = {
          labels,
          datasets: [
            {
              label: `Reporte ${period1}`,
              data: dataset1,
              backgroundColor: "rgba(54, 162, 235, 0.7)",
            },
            {
              label: `Reporte ${period2}`,
              data: dataset2,
              backgroundColor: "rgba(255, 159, 64, 0.7)",
            },
          ],
        };

        setChartData(newChartData);
      })
      .catch((err) => {
        console.error("Error al comparar reportes:", err);
      });
  };

  // 3) Obtener el TOP 10 RFM (filtrando por un reporte)
  const handleGetTop10 = (reportId) => {
    if (!reportId) {
      alert("Debes seleccionar un reporte para obtener el Top 10");
      return;
    }

    axiosInstance
      .get(`/reports/top10-rfm?reportId=${reportId}`)
      .then((res) => {
        // res.data => [{ rfmCode: "AAA", total: 50 }, { rfmCode: "AAB", total: 30 }, ...]
        const data = res.data || [];
        if (!Array.isArray(data) || data.length === 0) {
          setTop10ChartData(null);
          return;
        }

        // Construir el dataset
        const labels = data.map((item) => item.rfmCode);
        const totals = data.map((item) => item.total);

        const newTop10Data = {
          labels,
          datasets: [
            {
              label: "TOP 10 RFM",
              data: totals,
              backgroundColor: "rgba(75, 192, 192, 0.7)",
            },
          ],
        };
        setTop10ChartData(newTop10Data);
      })
      .catch((err) => {
        console.error("Error al obtener Top 10 RFM:", err);
      });
  };

  return (
    <div
      style={{
        background: "#1f1f1f",
        color: "#ccc",
        padding: "1rem",
        borderRadius: "8px",
        boxShadow: "0 0 10px rgba(0,0,0,0.5)",
        margin: "1rem 0",
        fontFamily: "'Segoe UI', Tahoma, sans-serif",
      }}
    >
      {/* =========================================
          SECCIÓN: COMPARACIÓN DE 2 REPORTES
        ========================================= */}
      <div style={{ marginBottom: "0.5rem", fontSize: "14px", color: "#fff" }}>
        <strong>Comparación RFM</strong>
      </div>

      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: "1rem",
          marginBottom: "1rem",
        }}
      >
        {/* Selección de Reporte 1 */}
        <div style={{ minWidth: "200px" }}>
          <span style={{ fontSize: "14px", fontWeight: 500, color: "#ccc" }}>
            Reporte 1:
          </span>
          <select
            value={reportId1}
            onChange={(e) => setReportId1(e.target.value)}
            style={{
              width: "100%",
              padding: "0.3rem",
              marginTop: "0.25rem",
              background: "#2a2a2a",
              color: "#ccc",
              border: "1px solid #444",
              borderRadius: "4px",
            }}
          >
            <option value="">-- Seleccionar --</option>
            {reports.map((r) => (
              <option key={r.id} value={r.id}>
                {r.id} - {r.month}/{r.year}
              </option>
            ))}
          </select>
        </div>

        {/* Selección de Reporte 2 */}
        <div style={{ minWidth: "200px" }}>
          <span style={{ fontSize: "14px", fontWeight: 500, color: "#ccc" }}>
            Reporte 2:
          </span>
          <select
            value={reportId2}
            onChange={(e) => setReportId2(e.target.value)}
            style={{
              width: "100%",
              padding: "0.3rem",
              marginTop: "0.25rem",
              background: "#2a2a2a",
              color: "#ccc",
              border: "1px solid #444",
              borderRadius: "4px",
            }}
          >
            <option value="">-- Seleccionar --</option>
            {reports.map((r) => (
              <option key={r.id} value={r.id}>
                {r.id} - {r.month}/{r.year}
              </option>
            ))}
          </select>
        </div>

        {/* Botón Comparar */}
        <div style={{ alignSelf: "flex-end" }}>
          <button
            onClick={handleCompare}
            disabled={!reportId1 || !reportId2}
            style={{
              padding: "0.5rem 1rem",
              cursor: !reportId1 || !reportId2 ? "not-allowed" : "pointer",
              backgroundColor: "#1890ff",
              border: "none",
              color: "#fff",
              borderRadius: "4px",
            }}
          >
            Comparar
          </button>
        </div>
      </div>

      {/* Gráfico de COMPARACIÓN */}
      <div
        style={{
          height: "400px",
          background: "#2a2a2a",
          padding: "1rem",
          borderRadius: "8px",
          marginBottom: "2rem",
        }}
      >
        {chartData ? (
          <>
            {/* Ajustamos el título de la gráfica desde las opciones */}
            {(options.plugins.title.text = "Comparación RFM de dos reportes")}
            <Bar data={chartData} options={options} />
          </>
        ) : (
          <p style={{ textAlign: "center", color: "#888" }}>
            Selecciona dos reportes para comparar.
          </p>
        )}
      </div>

      {/* =========================================
          SECCIÓN: TOP 10 RFM DE UN REPORTE
        ========================================= */}
      <div style={{ marginBottom: "0.5rem", fontSize: "14px", color: "#fff" }}>
        <strong>Top 10 RFM (por Reporte)</strong>
      </div>

      <div
        style={{
          display: "flex",
          gap: "1rem",
          marginBottom: "1rem",
        }}
      >
        {/* Reutilizas la selección de Reporte 1, por ejemplo.
            O podrías hacer otra <select> independiente. */}
        <div style={{ minWidth: "200px" }}>
          <span style={{ fontSize: "14px", fontWeight: 500, color: "#ccc" }}>
            Reporte para Top 10:
          </span>
          <select
            value={reportId1} // Podrías usar un state distinto, ej. top10ReportId
            onChange={(e) => setReportId1(e.target.value)}
            style={{
              width: "100%",
              padding: "0.3rem",
              marginTop: "0.25rem",
              background: "#2a2a2a",
              color: "#ccc",
              border: "1px solid #444",
              borderRadius: "4px",
            }}
          >
            <option value="">-- Seleccionar --</option>
            {reports.map((r) => (
              <option key={r.id} value={r.id}>
                {r.id} - {r.month}/{r.year}
              </option>
            ))}
          </select>
        </div>

        {/* Botón para cargar el TOP 10 */}
        <div style={{ alignSelf: "flex-end" }}>
          <button
            onClick={() => handleGetTop10(reportId1)}
            style={{
              padding: "0.5rem 1rem",
              backgroundColor: "#f2a30f",
              border: "none",
              color: "#fff",
              borderRadius: "4px",
              cursor: "pointer",
            }}
          >
            Ver Top 10
          </button>
        </div>
      </div>

      {/* Gráfico del TOP 10 */}
      <div
        style={{
          height: "400px",
          background: "#2a2a2a",
          padding: "1rem",
          borderRadius: "8px",
        }}
      >
        {top10ChartData ? (
          <>
            {
              (options.plugins.title.text =
                "Top 10 Códigos RFM (Reporte seleccionado)")
            }
            <Bar data={top10ChartData} options={options} />
          </>
        ) : (
          <p style={{ textAlign: "center", color: "#888" }}>
            Selecciona un reporte y haz clic en "Ver Top 10".
          </p>
        )}
      </div>
    </div>
  );
};

export default RFMComparisonDashboard;
